<script setup>
import SponsorsFilters from '../../components/forms/filters/Sponsorships.vue';
import urlPaginationRestore from '@/mixins/urlPaginationRestore';
import StatusPanel from '@/components/panels/Status.vue';
import PlatformIcon from '@/components/PlatformIcon.vue';
import columns from '../../config/sponsorships/columns';
</script>

<template>
  <div class="view-logo-sponsorship">
    <!-- route to AddNew popup [[src/components/dialog/sponsorships/AddNew.vue]] -->
    <!-- route to Edit popup [[src/components/dialog/sponsorships/Edit.vue]] -->
    <!-- All popups will appear here -->
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame
      has-before
      :title="$t('logo_sponsorship')"
      :total-count="pagination.totalItems"
    >
      <template v-slot:header-actions>
        <header-actions :actions="headerActions" />
      </template>

      <template v-slot:content>
        <SponsorsFilters
          ref="sponsorsfilter"
          :is-skeleton-loading="isSkeletonLoading"
          @filter="onFilter"
          @clearFilters="onClearFilters"
        >
          <template v-slot:phone-actions>
            <sort-by-select
              :columns="columns"
              :pagination="pagination"
              :on-update-pagination="onUpdatePagination"
            />
          </template>
        </SponsorsFilters>

        <table-base
          has-phone-layout
          :on-add="addAction?.onClick"
          :add-label="addAction?.title"
          :columns="columns"
          class="logo-sponsorship-table"
          :items="sponsors"
          :pagination="pagination"
          :pages="totalPages"
          :is-loading="isLoading"
          :can-edit-columns="false"
          :on-select-item="selectSponsor"
          :on-change-page="onChangePage"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
          :no-data-title="$t('no_sponsorships')"
          :no-data-subtitle="$t('start_to_use_our_products')"
          has-custom-items-template
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'country.platform'">
              <span class="d-flex align-center">
                <PlatformIcon
                  :country="scope.cell.item.country.code"
                  class="flex-shrink-0"
                />
                <span class="pl-2">
                  {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
                </span>
              </span>
            </template>

            <StatusPanel
              v-else-if="scope.cell.column.value === 'status'"
              table-group="sponsorships"
              :status="scope.cell.item.status.name"
            />

            <template v-else-if="scope.cell.column.value === 'categories'">
              <div :class="{
               'logo-sponsorship-table__categories': true,
               'flex-wrap': expandedRows.includes(scope.cell.item.id),
              }">
                <v-chip
                  v-for="(item, index) in scope.cell.item.categories"
                  v-show="isCategoryShown(scope.cell.item, index)"
                  color="#f5f5f5"
                  :key="item.id"
                  variant="flat"
                  density="compact"
                >
                  {{ item.name }}
                </v-chip>
                <!-- A phone card can't expand in place; the rest are in the details popup. -->
                <v-chip
                  v-if="$vuetify.display.xs && scope.cell.item.categories.length > 3"
                  color="#f5f5f5"
                  variant="flat"
                  density="compact"
                  @click.stop="selectSponsor(scope.cell.item)"
                >
                  {{ $t('n_more', { count: scope.cell.item.categories.length - 2 }) }}
                </v-chip>
                <v-icon v-else-if="!$vuetify.display.xs && scope.cell.item.categories.length > 2" @click="toggleExpandCategories($event, scope.cell.item.id)">
                  {{ expandedRows.includes(scope.cell.item.id) ? 'expand_less' : 'expand_more' }}
                </v-icon>
              </div>
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
import { isStr, extractNestedProp } from '@/utils';
import defaultPagination from '@/config/pagination';

export default {
  mixins: [urlPaginationRestore],
  data() {
    return {
      totalPages: 0,
      pagination: {
        ...defaultPagination,
        rowsPerPage: defaultPagination.perPage,
        order: 'desc' ,
        sortBy: 'dates',
      },
      query: {},
      selectedSponsorship: {},
      createdSponsor: {},
      expandedRows: [],
      isSkeletonLoading: true,
      isLoading: true,
    };
  },
  computed: {
    addAction() {
      return this.headerActions.find(({ key, isDisabled }) => key === 'add' && !isDisabled);
    },
    headerActions() {
      return [
        this.$ability.can('view', 'ad-manager.order') && {
          key: 'add',
          title: this.$t('new_logo_sponsorship'),
          onClick: this.openNewSponsorModal,
        },
      ].filter(Boolean);
    },
    sponsors() {
      return this.$store.getters['sponsorships/items'];
    },
    totalCount() {
      return this.pagination.totalItems || 0;
    },
  },
  methods: {
    // Emitted by the dialog routes after a successful save. Keeps the
    // refetch in this view, with this view's own query, and out of the URL.
    onSaved() {
      this.isLoading = true;
      this.getLogoSponsorships();
    },
    toggleExpandCategories(e, itemId) {
      e.stopPropagation();

      const itemIndex = this.expandedRows.findIndex(i => i === itemId);

      if (itemIndex > -1) {
        this.expandedRows.splice(itemIndex, 1);
      } else {
        this.expandedRows.push(itemId);
      }
    },
    // On a phone, three categories fit; past that, two and a "+N more" chip.
    isCategoryShown(item, index) {
      if (this.$vuetify.display.xs) {
        return item.categories.length <= 3 || index < 2;
      }

      return index < 2 || this.expandedRows.includes(item.id);
    },
    // Set the max-width property for categories column, because onExpandCategories method is moving the column
    setCategoryColumnMaxWidth() {
      // The phone cards wrap the categories, and a fixed width would run a
      // cell out of its card.
      if (this.$vuetify.display.xs) return;

      const elements = document.getElementsByClassName('logo-sponsorship-table__categories');

      if (elements.length) {
        const firstElement = elements[0];
        const paddings = firstElement.offsetLeft * 2;

        // ...[...elements] is used to convert HTMLCollection to Array
        const elementWidth = Math.max(...[...elements].map(el => el.offsetWidth));

        for (var i = 0; i < elements.length; i++) {
          // Set the max-width property for each element
          elements[i].style.maxWidth = elementWidth + 'px';
          elements[i].parentNode.style.width = elementWidth + paddings + 'px';
        }
      }
    },
    getLogoSponsorships() {
      const { page, perPage } = this.pagination;

      const query = {
        ...this.query,
        page,
        per_page: perPage,
      };

      return this.$store.dispatch('sponsorships/getLogoSponsorships', query)
        .then(({ data, meta }) => {
          this.totalPages = meta?.last_page || 0;
          this.pagination.page = meta?.current_page || 0;
          this.pagination.totalItems = meta?.total || 0;
          this.pagination.lastPage = meta?.last_page || 0;

          this.$nextTick(() => {
            this.setCategoryColumnMaxWidth();
          });

          return data;
        })
        .finally(() => {
          this.isLoading = false;
          this.isSkeletonLoading = false;
        });
    },
    onFilter(filters) {
      this.query = {
        ...this.query,
        ...filters,
      };
      this.pagination.page = 1;

      this.runInitialFetch(() => this.getLogoSponsorships());
    },
    onClearFilters() {
      const { sort, per_page, order} = this.query; // save the current sort, per_page and order values
      this.query = { sort, page: 1, per_page, order }; // reset the query object
      this.pagination.page = 1;

      this.getLogoSponsorships();
    },
    openNewSponsorModal() {
      this.$router.push({
        path: `/sponsorships/new`,
        query: this.$route.query,
      });
    },
    openEditSponsorModal() {
        this.$router.push({
          path: `/sponsorships/${this.selectedSponsorship.id}`,
          query: this.$route.query,
        });
    },
    displayColumnValue(item, columnValue) {
      return extractNestedProp(item, columnValue);
    },
    selectSponsor(sponsor) {
      this.selectedSponsorship = sponsor;
      this.openEditSponsorModal();

      // return this.$store.dispatch('sponsorships/setActiveSponsor', sponsor);
    },
    onChangePage(page) {
      const { query, pagination } = this;
      const { order, sortBy } = pagination;
      this.pagination.page = page;
      this.query = {
        ...query,
        page,
        order,
        sort: sortBy
      };

      this.getLogoSponsorships();
    },
    onUpdatePagination({ page, order, sortBy }) {
      if (!sortBy) return null;

      this.pagination.page = page;
      this.pagination.order = order;
      this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

      const { query, pagination } = this;
      this.query = {
        ...query,
        page,
        order,
        sort: isStr(sortBy) ? sortBy : pagination.sortBy
      };

      this.runInitialFetch(() => this.getLogoSponsorships());
    },
  },
};
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

.logo-sponsorship-table {
  &__categories {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;

    // A phone card is too narrow for two categories side by side.
    @include phone {
      flex-wrap: wrap;
    }

    .v-icon {
      cursor: pointer;
    }
  }
}
</style>
